import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface PaymentRecord {
  id: number;
  paymentType: string;
  amount: number;
  paymentDate: string;
  receiptFileName?: string;
  accountantApproved: boolean;
  trackingCode?: string;
  depositDate?: string;
}

export interface ProformaInvoice {
  id: number;
  invoiceNumber: number;
  customerName: string;
  date: string;
  validityDate: string;
  saleType: string;
  currency: string;
  itemCode: string;
  itemTitle: string;
  quantity: number;
  length: number;
  width: number;
  unitPrice: number;
  status: "پیش فاکتور" | "سفارش فروش";
  panelType: string;
  thickness: number;
  topSheet: string;
  backSheet: string;
  topSheetThickness: string;
  backSheetThickness: string;
  foamDensity: string;
  foamType: string;
  topSheetColor: string;
  backSheetColor: string;
  description: string;
  area: number;
  totalAmount: number;
  packagingCost: number;
  vat: number;
  netAmount: number;
  registeredBy: string;
  registeredAt: string;
  salesManagerApproval: boolean;
  ceoApproval: boolean;
  payments: PaymentRecord[];
  // مبلغ واریزی تایید شده: انباشتی، هر بار حسابدار پرداختی را تایید می‌کند به آن اضافه می‌شود
  approvedDepositedAmount: number;
}

export interface BusinessState {
  invoices: ProformaInvoice[];
  // داده‌های اولیه > حداقل پرداختی (درصد)
  minPaymentPercent: number;
  minWithSalesManagerApproval: number;
  maxWithSalesManagerApproval: number;
}

// TODO: این رکورد نمونه فقط برای نمایش عملکرد صفحه «سفارش‌های فروش» است؛
// بعداً که به API واقعی وصل شد، حذف شود
const seedOrder: ProformaInvoice = {
  id: 1,
  invoiceNumber: 1,
  customerName: "علی احمدی",
  date: "1403/05/01",
  validityDate: "1403/05/02",
  saleType: "فروش داخلی",
  currency: "ریال",
  itemCode: "010511484848199",
  itemTitle: "پانل دیواری 5 سانت گالوانیزه",
  quantity: 10,
  length: 2,
  width: 1.2,
  unitPrice: 1500000,
  status: "سفارش فروش",
  panelType: "دیواری",
  thickness: 5,
  topSheet: "گالوانیزه",
  backSheet: "گالوانیزه",
  topSheetThickness: "48 ± 2",
  backSheetThickness: "48 ± 2",
  foamDensity: "48 ± 2",
  foamType: "PUR",
  topSheetColor: "سفید",
  backSheetColor: "سفید",
  description: "دیواری 5 سانت",
  area: 24,
  totalAmount: 36000000,
  packagingCost: 360000,
  vat: 3636000,
  netAmount: 39996000,
  registeredBy: "کارشناس فروش نمونه",
  registeredAt: "1403/05/01 10:30",
  salesManagerApproval: true,
  ceoApproval: false,
  approvedDepositedAmount: 39996000,
  payments: [
    {
      id: 1,
      paymentType: "نقدی",
      amount: 39996000,
      paymentDate: "1403/05/01",
      accountantApproved: true,
      trackingCode: "123456",
      depositDate: "1403/05/01",
    },
  ],
};

export const initialState: BusinessState = {
  invoices: [seedOrder],
  minPaymentPercent: 10,
  minWithSalesManagerApproval: 5,
  maxWithSalesManagerApproval: 30,
};

const businessSlice = createSlice({
  name: "Business",
  initialState,
  reducers: {
    setMinimumPaymentSettings(
      state,
      action: PayloadAction<{
        minPaymentPercent: number;
        minWithSalesManagerApproval: number;
        maxWithSalesManagerApproval: number;
      }>
    ) {
      state.minPaymentPercent = action.payload.minPaymentPercent;
      state.minWithSalesManagerApproval =
        action.payload.minWithSalesManagerApproval;
      state.maxWithSalesManagerApproval =
        action.payload.maxWithSalesManagerApproval;
    },
    addInvoice(state, action: PayloadAction<ProformaInvoice>) {
      state.invoices.push(action.payload);
    },
    removeInvoice(state, action: PayloadAction<number>) {
      state.invoices = state.invoices.filter((i) => i.id !== action.payload);
    },
    addPayment(
      state,
      action: PayloadAction<{ invoiceId: number; payment: PaymentRecord }>
    ) {
      const inv = state.invoices.find(
        (i) => i.id === action.payload.invoiceId
      );
      if (inv) inv.payments.push(action.payload.payment);
    },
    removePayment(
      state,
      action: PayloadAction<{ invoiceId: number; paymentId: number }>
    ) {
      const inv = state.invoices.find(
        (i) => i.id === action.payload.invoiceId
      );
      if (inv) {
        inv.payments = inv.payments.filter(
          (p) => p.id !== action.payload.paymentId
        );
      }
    },
    // تایید حسابدار: کد رهگیری و تاریخ واریز را ثبت می‌کند، مبلغ را به مبلغ واریزی تایید‌شده اضافه می‌کند
    // و طبق فرمول M >= h * f در صورت لزوم وضعیت را به «سفارش فروش» تغییر می‌دهد
    approvePayment(
      state,
      action: PayloadAction<{
        invoiceId: number;
        paymentId: number;
        trackingCode: string;
        depositDate: string;
      }>
    ) {
      const inv = state.invoices.find(
        (i) => i.id === action.payload.invoiceId
      );
      if (!inv) return;
      const payment = inv.payments.find(
        (p) => p.id === action.payload.paymentId
      );
      if (!payment || payment.accountantApproved) return;
      payment.accountantApproved = true;
      payment.trackingCode = action.payload.trackingCode;
      payment.depositDate = action.payload.depositDate;
      if (payment.paymentType === "نقدی") {
        inv.approvedDepositedAmount += payment.amount;
      }
      const h = state.minPaymentPercent / 100;
      if (
        inv.status === "پیش فاکتور" &&
        inv.approvedDepositedAmount >= h * inv.netAmount
      ) {
        inv.status = "سفارش فروش";
      }
    },
    // تایید مدیرفروش: یک‌طرفه است. اگر h1*f <= M <= h2*f باشد وضعیت به سفارش فروش تغییر می‌کند
    approveSalesManager(state, action: PayloadAction<number>) {
      const inv = state.invoices.find((i) => i.id === action.payload);
      if (!inv) return;
      inv.salesManagerApproval = true;
      const h1 = state.minWithSalesManagerApproval / 100;
      const h2 = state.maxWithSalesManagerApproval / 100;
      const M = inv.approvedDepositedAmount;
      if (
        inv.status === "پیش فاکتور" &&
        M >= h1 * inv.netAmount &&
        M <= h2 * inv.netAmount
      ) {
        inv.status = "سفارش فروش";
      }
    },
    approveCeo(state, action: PayloadAction<number>) {
      const inv = state.invoices.find((i) => i.id === action.payload);
      if (inv) inv.ceoApproval = true;
    },
  },
});

export const {
  setMinimumPaymentSettings,
  addInvoice,
  removeInvoice,
  addPayment,
  removePayment,
  approvePayment,
  approveSalesManager,
  approveCeo,
} = businessSlice.actions;

export default businessSlice.reducer;
